import { Page, pickSeoProps } from '@components/Page'
import { Breadcrumbs } from '@components/_rcpo/Breadcrumbs'
import { RCPOPage } from '@components/_rcpo/Page'
import { RCPOProductOverview } from '@components/_rcpo/ProductOverview'
import {
  RCPO_ATTRIBUTE_ID_COLLECTION,
  useRCPOData,
} from '@components/_rcpo/ProductOverview/hooks/use-rcpo-data'
import { useSetFilterParameter } from '@components/_rcpo/ProductOverview/hooks/use-set-filter-parameter'
import KeepExploring from '@components/_rcpo/Sliders/KeepExploring'
import TextBlock from '@components/_rcpo/TextBlock'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { getSelectedCollectionSlug } from '@lib/get-selected-collection-slug'
import { SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { RolexRcpoSelectionPage } from '@lib/kontent/models/RolexRcpoSelectionPage'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useMemo } from 'react'

type RCPOWatchesProps = SingleItemResponse<RolexRcpoSelectionPage>

const RCPOWatches: NextPage<RCPOWatchesProps> = ({ item, modular_content }) => {
  const router = useRouter()
  const { t } = useTranslation('navigation')
  const keepExploringItems = item.elements.keep_exploring_items.value.map(
    (key) => modular_content[key].elements,
  )

  const { filters } = useRCPOData()
  const { activeFilters: activeCollectionFilters } = useSetFilterParameter(
    RCPO_ATTRIBUTE_ID_COLLECTION,
  )
  const collectionFilter = filters.find(
    (filter) => filter.attributeID === RCPO_ATTRIBUTE_ID_COLLECTION,
  )

  const selectedCollection = useMemo(() => {
    if (!collectionFilter?.articleFilterAttributeValues) return null

    return getSelectedCollectionSlug(
      collectionFilter?.articleFilterAttributeValues,
      activeCollectionFilters,
    )
  }, [activeCollectionFilters, collectionFilter?.articleFilterAttributeValues])

  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateNavigation(router.locales, 'rcpo.selection.url')}
    >
      <RCPOPage pageType="selection page" productFamily={selectedCollection}>
        <Breadcrumbs items={['index']} currentPageTitle={t('rcpo.selection.title')} />
        <TextBlock
          headingTag="h2"
          preTitle="Rolex Certified Pre-Owned"
          title={item.elements.title.value}
          description={item.elements.description.value}
          className="mb-8 pt-8 sm:mb-16 sm:pt-16"
        />
        <RCPOProductOverview />
        <KeepExploring
          items={keepExploringItems}
          title={item.elements.keep_exploring_title.value}
        />
      </RCPOPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  try {
    const content = await getPageByCodename<RolexRcpoSelectionPage>({
      codename: 'rcpo___watches_page',
      lang: locale as string,
      preview,
    })

    return {
      props: {
        ...content,
        ...(await getServerSideTranslations(locale as string)),
      },
    }
  } catch (error) {
    throw error
  }
}

export default RCPOWatches
